import React from "react";
import { useSelector } from "react-redux";
import { Box } from "@mui/material";
import SideNav from "../../../components/commonComponents/Sidebar/SideNav";
import { StyledMainBox, StickyTopRowStackSpaceBetween } from "../../../components/commonComponents/StyledComponents/styled";

import { checkUserDetailsFromCookie } from "../../../api_calls/userManagement";
import Grid from "@mui/material/Unstable_Grid2";
import DateRangeFilter from "../../../components/commonComponents/Filters/DateRangeFilter";
import {
  clearfilters,
  fetchCampaignNames,
} from "../../../api_calls/campaignRequests";
import ClipNameFilter from "../../../components/commonComponents/Filters/ClipNameFilter";
import CampaignNameFilter from "../../../components/commonComponents/Filters/CampaignNameFilter";
import { TextButton } from "../../../components/commonComponents/Buttons/ActionButton";
import { useNavigate } from "react-router-dom";
import { fetchClipNames } from "../../../api_calls/clipRequests";

import { dbFormatDate, formatToStandardDate, standardFormatToday, getFirstDayTwoMonthsAgo, getFirstDayEightMonthsAgo, findEmbeddableId, getFirstDayMonthsAfter } from "../../../api_calls/utils";
import Dashboard from "../../../components/commonComponents/Embeddable/Dashboard";
import DateRangeInputFields from "../../../components/commonComponents/Filters/DateRangeInputFields";
import { setBatchLoadingStatus } from "../../../api_calls/requestConfig";
import AccessControl from "../../../components/commonComponents/Hoc/AccessControl";

const ClipOverview = () => {
  // Variables
  const userStateDate = useSelector((state) => state.userSelections);
  const [userInfo, setUserInfo] = React.useState({});

  // const firstDayTwoMonthsAgo = formatToStandardDate(getFirstDayTwoMonthsAgo());
  // // make it the actual first day of the month instead of the last day of the month before
  // firstDayTwoMonthsAgo.setDate(firstDayTwoMonthsAgo.getDate() + 0);

  const firstDayTwoMonthsAgo = React.useMemo(() => {
    const date = formatToStandardDate(getFirstDayTwoMonthsAgo());
    date.setDate(date.getDate()); // same as +0
    return date;
  }, []);

  const firstDayThreeMonthsAfter = React.useMemo(() => {
    const date = formatToStandardDate(getFirstDayMonthsAfter(3));
    date.setDate(date.getDate()); // same as +0
    return date;
  }, []);

  const forecastEnabled = typeof userStateDate.campaign_overview_forecast !== "undefined" ? userStateDate.campaign_overview_forecast : false

  const { minSelectableDate, maxSelectableDate } = React.useMemo(() => {

    const today = standardFormatToday();
    const yesterday = formatToStandardDate(today);
    yesterday.setDate(today.getDate() - 1);

    const startDate = firstDayTwoMonthsAgo;
    const end = forecastEnabled ? firstDayThreeMonthsAfter : dbFormatDate(yesterday);

    return {
      minSelectableDate: startDate,
      maxSelectableDate: end,
    };
  }, [firstDayTwoMonthsAgo, forecastEnabled]);

  const navigate = useNavigate();

  const dashboardVariables = () => {
    let overviewDashboardVariables = {};

    if (typeof userStateDate.campaign_overview_selection !== "undefined" && userStateDate.campaign_overview_selection.length !== 0) {
      overviewDashboardVariables = {
        ...overviewDashboardVariables,
        IdCampaign: userStateDate.campaign_overview_selection,
      }
    }

    if (typeof userStateDate.campaign_overview_clip_selection !== "undefined") {
      if (typeof userStateDate.campaign_overview_show_clips !== "undefined" && userStateDate.campaign_overview_show_clips === true && userStateDate.campaign_overview_clip_selection.length !== 0) {
        overviewDashboardVariables = {
          ...overviewDashboardVariables,
          IdClip: userStateDate.campaign_overview_clip_selection,
        }
      }

    }

    if (typeof userStateDate.campaign_overview_show_clips !== "undefined") {
      overviewDashboardVariables = {
        ...overviewDashboardVariables,
        ShowClips: userStateDate.campaign_overview_show_clips,
      }
    }

    // forecast
    if (typeof userStateDate.campaign_overview_forecast !== "undefined") {
      overviewDashboardVariables = {
        ...overviewDashboardVariables,
        ShowForecast: userStateDate.campaign_overview_forecast,
      }
    }

    if (typeof userStateDate.campaign_overview_selected_dates !== "undefined") {
      const endDate = new Date(userStateDate?.campaign_overview_selected_dates.to);
      endDate.setDate(endDate.getDate() + 1); // add one day to
      overviewDashboardVariables = {
        ...overviewDashboardVariables,
        Date: {
          from: userStateDate?.campaign_overview_selected_dates.from,
          to: endDate,
        },
      }
    }

    return overviewDashboardVariables;
  }

  React.useEffect(() => {
    const checkUserDetails = checkUserDetailsFromCookie();

    let userDetailObj = {};
    if (checkUserDetails.success === true) {
      userDetailObj = checkUserDetails.user_detail;
    }
    setUserInfo(userDetailObj);

    const fetchData = async () => {
      setBatchLoadingStatus(true)
      Promise.all([fetchCampaignNames(), fetchClipNames()]).then(() => setBatchLoadingStatus(false));
    };
    fetchData();
    clearfilters();
  }, []);

  // Elements
  return (
    <>
      {typeof userStateDate.campaign_names_info !== "undefined" && typeof userStateDate.clip_names_info !== "undefined" && (
        <StyledMainBox component="main">
          <StickyTopRowStackSpaceBetween direction={"row"} spacing={2} sx={{ mr: 0 }}>
            {/* ------- Back to campaigns button ----------- */}
            <Box sx={{ alignContent: "center" }}>
              <TextButton
                onClick={(e) => {
                  // e.preventDefault();
                  navigate("/check-campaigns/list");
                }}
                label={"Back to Contact-based Campaigns"}
              />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", justifyContent: "flex-end" }}>
              {/* ------- Clip Name/ clips boolean Filter ----------- */}
              <ClipNameFilter usedFor={"clip-overview"} reduxKeywordToSaveValue={"campaign_overview_clip_selection"} />
              {/* ------- Campaign Name Filter ----------- */}
              {typeof userStateDate.campaign_names_info.campaign_name_obj !== "undefined" ? (
                <Box sx={{ width: 250, alignContent: "center" }}>
                  <CampaignNameFilter usedFor={"clip-overview"} fieldName="id_campaign" vizId={"campaignMonitoringViz"} />
                </Box>
              ) : (
                ""
              )}
              {/* ------------ day timestamp filter ------------ */}
              <Box sx={{ alignSelf: "center", pt: 3 }}>
                <DateRangeInputFields
                  minSelectableDate={minSelectableDate}
                  maxSelectableDate={maxSelectableDate}
                  reduxKeywordToSaveValue={"campaign_overview_selected_dates"}
                />
              </Box>
            </Box>
          </StickyTopRowStackSpaceBetween>
          <Box height={1} />
          <Grid container spacing={2}>
            {typeof userInfo.root_company_id !== "undefined" && userInfo.root_company_id !== "" && (
              <Dashboard
                embedDashboardId={findEmbeddableId("brand_analytics", "campaign_overview")}
                variables={dashboardVariables()}
                filterConfig={{
                  "number_root": userInfo.root_company_id.toString(),
                }} />
            )}
          </Grid>
        </StyledMainBox>
      )}
    </>
  );
};

export default AccessControl(ClipOverview, ["campaign_dashboard_overview_can_view"], [], ["campaigns_section", "campaign_list"]);
